import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Filter,
  Download,
  Power,
  PowerOff,
  Unplug,
  MoreVertical
} from 'lucide-react';
import { PropBotConnectedAccount } from '@/hooks/use-prop-bot-detail-admin';

interface AccountOperationsToolbarProps {
  accounts: PropBotConnectedAccount[];
  selectedAccounts: Set<string>;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  accountTypeFilter: string;
  onAccountTypeFilterChange: (type: string) => void;
  onBulkEnable: () => void;
  onBulkDisable: () => void;
  onBulkDisconnect: () => void;
  onExport: () => void;
  isProcessing: boolean;
}

export const AccountOperationsToolbar: React.FC<AccountOperationsToolbarProps> = ({
  accounts,
  selectedAccounts,
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  accountTypeFilter,
  onAccountTypeFilterChange,
  onBulkEnable,
  onBulkDisable,
  onBulkDisconnect,
  onExport,
  isProcessing
}) => {
  const hasSelection = selectedAccounts.size > 0;

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search accounts by user name, email, or account number..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="w-32">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Select value={accountTypeFilter} onValueChange={onAccountTypeFilterChange}>
            <SelectTrigger className="w-32">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="demo">Demo</SelectItem>
              <SelectItem value="reverse">Reverse</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk Operations */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="text-sm text-muted-foreground">
          {hasSelection ? (
            <span className="font-medium text-primary">
              {selectedAccounts.size} account{selectedAccounts.size !== 1 ? 's' : ''} selected
            </span>
          ) : (
            <span>
              {accounts.length} account{accounts.length !== 1 ? 's' : ''} total
            </span>
          )}
        </div>

        <div className="flex gap-2">
          {hasSelection && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkEnable}
                disabled={isProcessing}
                className="text-green-600 hover:text-green-700"
              >
                <Power className="h-4 w-4 mr-2" />
                Enable ({selectedAccounts.size})
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkDisable}
                disabled={isProcessing}
                className="text-orange-600 hover:text-orange-700"
              >
                <PowerOff className="h-4 w-4 mr-2" />
                Disable ({selectedAccounts.size})
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkDisconnect}
                disabled={isProcessing}
                className="text-destructive hover:text-destructive/90"
              >
                <Unplug className="h-4 w-4 mr-2" />
                Disconnect ({selectedAccounts.size})
              </Button>
            </>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreVertical className="h-4 w-4 mr-2" />
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onExport}>
                <Download className="h-4 w-4 mr-2" />
                Export to CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};